import { addTzAdjustedDays, getTzAdjustedStartOfDay } from '../date-tz-helpers/index.js';
/** Default term length in months used for unlimited duration seats (100 years) */
export const DEFAULT_UNLIMITED_TERM_LENGTH = 1200;
/**
 * Checks if a seat has unlimited term duration.
 * @param termLength - The term length value
 * @returns True if the term length represents unlimited duration (-1)
 */
export const isUnlimitedTermDuration = (termLength) => termLength === -1;
/**
 * Returns the effective term length for date calculations.
 * Substitutes -1 (unlimited) with the default unlimited term length so that
 * standard addMonths logic works without special-casing.
 * @param termLength - The term length value (-1 for unlimited, or positive months)
 * @returns The term length to use in date calculations
 */
export const effectiveTermLength = (termLength) => isUnlimitedTermDuration(termLength) ? DEFAULT_UNLIMITED_TERM_LENGTH : termLength;
export const getSeatTypeName = (type) => {
    switch (type) {
        case 'voting':
            return 'Voting';
        case 'non_voting':
            return 'Non-Voting';
        case 'ex_officio':
            return 'Ex-Officio';
        default:
            return type;
    }
};
export const getSeatTypeVariant = (type) => {
    switch (type) {
        case 'voting':
            return 'info-light';
        case 'non_voting':
            return 'warning-light';
        case 'ex_officio':
            return 'info-alt-light';
        default:
            return 'muted';
    }
};
export const SEAT_TYPE_OPTIONS = [
    { label: getSeatTypeName('voting'), value: 'voting' },
    { label: getSeatTypeName('non_voting'), value: 'non_voting' },
    { label: getSeatTypeName('ex_officio'), value: 'ex_officio' },
];
export const getTermLimitLabel = (termLimit) => {
    if (termLimit === -1) {
        return 'No term limit';
    }
    return `${termLimit} ${termLimit === 1 ? 'term' : 'terms'}`;
};
/**
 * Checks if a current term is open for applications.
 * A current term is open if it's vacant and has started (startDate is in the past or present).
 * This is the counterpart to isUpcomingTermOpen for current terms.
 *
 * @param status - The term status
 * @param startDate - The start date of the current term
 * @param applicationOpenDays - Number of days for the application window (used for consistency with upcoming term logic)
 * @param timezone - The timezone to use for date calculations
 * @returns True if the current term is open for applications
 */
export const isCurrentTermOpen = (status, startDate, applicationOpenDays, timezone) => {
    if (status !== 'vacant') {
        return false;
    }
    if (!startDate) {
        return false;
    }
    // Start of day is used to provide consistent results throughout the day. Updates will only happen
    // at midnight. This is also needed because stored start/end dates don't have hour/time precision.
    const today = getTzAdjustedStartOfDay(new Date(), timezone);
    const termStartDate = getTzAdjustedStartOfDay(new Date(startDate), timezone);
    const applicationWindowDate = addTzAdjustedDays(today, applicationOpenDays, timezone);
    // Current term is open if it's vacant and has started (startDate <= applicationWindowDate)
    // For current terms, startDate is typically in the past, so this check ensures the term has begun
    return termStartDate <= applicationWindowDate;
};
/**
 * Checks if an upcoming term is open for applications based on its start date.
 * An upcoming term is open if it's vacant and startDate is within the application window.
 *
 * @param status - The term status
 * @param startDate - The start date of the upcoming term (same as current term's end date)
 * @param applicationOpenDays - Number of days before term start that applications open
 * @param timezone - The timezone to use for date calculations
 * @returns True if the upcoming term is open for applications
 */
export const isUpcomingTermOpen = (status, startDate, applicationOpenDays, timezone) => {
    if (status !== 'vacant') {
        return false;
    }
    const today = getTzAdjustedStartOfDay(new Date(), timezone);
    const transitionDate = getTzAdjustedStartOfDay(new Date(startDate), timezone);
    const applicationWindowDate = addTzAdjustedDays(today, applicationOpenDays, timezone);
    // Term is open if transition is within the application window (≤ applicationOpenDays away)
    // Also open if transition date has already passed but term hasn't been promoted yet, since
    // there is no automated term rotation.
    return transitionDate <= applicationWindowDate;
};
/**
 * Checks if an upcoming term is opening soon.
 * A term is opening soon if it's vacant and the term start is between
 * (applicationOpenDays + 1) and termExpirationDays away.
 *
 * @param status - The term status
 * @param startDate - The start date of the upcoming term (same as current term's end date)
 * @param applicationOpenDays - Number of days before term start that applications open
 * @param termExpirationDays - Number of days before term start that "Soon" appears
 * @param timezone - The timezone to use for date calculations
 * @returns True if the upcoming term is opening soon
 */
export const isUpcomingTermOpeningSoon = (status, startDate, applicationOpenDays, termExpirationDays, timezone) => {
    if (status !== 'vacant') {
        return false;
    }
    const today = getTzAdjustedStartOfDay(new Date(), timezone);
    const transitionDate = getTzAdjustedStartOfDay(new Date(startDate), timezone);
    const applicationWindowDate = addTzAdjustedDays(today, applicationOpenDays, timezone);
    const expirationThreshold = addTzAdjustedDays(today, termExpirationDays, timezone);
    // Show "Soon" if transition is > applicationOpenDays AND ≤ termExpirationDays (e.g., 61-90 days)
    return transitionDate > applicationWindowDate && transitionDate <= expirationThreshold;
};
